<template>
  <HrPageLayout
    title="Unused Leave Approvals"
    subtitle="Director HR · Welfare · HR Admin"
    header-tone="institutional"
    :breadcrumb-items="[
      { label: 'HR', route: '/dashboard' },
      { label: 'Unused Leave Approvals' }
    ]"
  >
    <div class="tabs">
      <button
        v-if="canDirector"
        type="button"
        class="tab"
        :class="{ on: tab === 'director' }"
        @click="tab = 'director'; load()"
      >
        Director HR queue
      </button>
      <button
        v-if="canWelfare"
        type="button"
        class="tab"
        :class="{ on: tab === 'welfare' }"
        @click="tab = 'welfare'; load()"
      >
        Welfare queue
      </button>
      <button
        v-if="canAdmin"
        type="button"
        class="tab"
        :class="{ on: tab === 'admin' }"
        @click="tab = 'admin'; load()"
      >
        All submissions
      </button>
    </div>

    <div v-if="!canDirector && !canWelfare && !canAdmin" class="card warn">
      You do not have an approver role for this module. Required permissions: UNUSED LEAVE APPROVE DIRECTOR HR, UNUSED LEAVE
      APPROVE WELFARE, or UNUSED LEAVE ADMIN / MANAGE LEAVE.
    </div>

    <div v-else class="card">
      <div class="toolbar">
        <button type="button" class="btn ghost" :disabled="loading" @click="load">{{ loading ? "Loading…" : "Refresh" }}</button>
      </div>
      <div v-if="!claims.length && !loading" class="muted">No records in this queue.</div>
      <div v-else class="table-wrap">
        <table class="tbl">
          <thead>
            <tr>
              <th>ID</th>
              <th>Employee</th>
              <th>Type</th>
              <th>Days</th>
              <th>Status</th>
              <th>Remarks</th>
              <th v-if="tab !== 'admin'">Action</th>
              <th v-if="tab === 'admin'">HR override</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in claims" :key="c.id">
              <td>{{ c.id }}</td>
              <td>{{ c.employeeNames }}<br /><small class="muted">{{ c.nationalId }}</small></td>
              <td>{{ c.leaveTypeName }}</td>
              <td>{{ c.daysClaimed }}</td>
              <td>{{ c.status }}</td>
              <td class="small">{{ shortText(c.remarks) }}</td>
              <td v-if="tab === 'director' && c.status === 'pending_director'">
                <button type="button" class="link" @click="openAct(c, 'approve_director')">Approve</button>
                ·
                <button type="button" class="link bad" @click="openAct(c, 'reject_director')">Reject</button>
              </td>
              <td v-else-if="tab === 'welfare' && c.status === 'pending_welfare'">
                <button type="button" class="link" @click="openAct(c, 'approve_welfare')">Approve</button>
                ·
                <button type="button" class="link bad" @click="openAct(c, 'reject_welfare')">Reject</button>
              </td>
              <td v-else-if="tab === 'director' || tab === 'welfare'">—</td>
              <td v-else-if="tab === 'admin'">
                <button type="button" class="link" @click="openAct(c, 'hr_force_approve')">Force approve</button>
                ·
                <button type="button" class="link bad" @click="openAct(c, 'hr_force_reject')">Force reject</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="modal.open" class="modal">
      <div class="modal-card">
        <h3>{{ modalTitle }}</h3>
        <p class="muted small">Claim #{{ modal.claim?.id }} — {{ modal.claim?.leaveTypeName }} ({{ modal.claim?.daysClaimed }} days)</p>
        <label class="lbl">Comments / reason</label>
        <textarea v-model="modal.remarks" class="inp ta" rows="3"></textarea>
        <div class="modal-actions">
          <button type="button" class="btn ghost" @click="modal.open = false">Cancel</button>
          <button type="button" class="btn primary" :disabled="modal.saving" @click="confirmAct">
            {{ modal.saving ? "Saving…" : "Confirm" }}
          </button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

export default {
  name: "UnusedLeaveApprovals",
  components: { HrPageLayout },
  data() {
    return {
      tab: "director",
      claims: [],
      loading: false,
      modal: { open: false, claim: null, action: "", remarks: "", saving: false }
    };
  },
  computed: {
    canDirector() {
      return getRight("UNUSED LEAVE APPROVE DIRECTOR HR");
    },
    canWelfare() {
      return getRight("UNUSED LEAVE APPROVE WELFARE");
    },
    canAdmin() {
      return getRight(["UNUSED LEAVE ADMIN", "MANAGE LEAVE"]);
    },
    modalTitle() {
      const a = this.modal.action || "";
      if (a.includes("reject")) return "Reject claim";
      if (a.includes("approve")) return "Approve claim";
      return "Process claim";
    }
  },
  mounted() {
    if (this.canDirector) this.tab = "director";
    else if (this.canWelfare) this.tab = "welfare";
    else if (this.canAdmin) this.tab = "admin";
    this.load();
  },
  methods: {
    shortText(s) {
      const t = String(s || "");
      return t.length > 80 ? `${t.slice(0, 77)}…` : t;
    },
    async load() {
      if (!this.canDirector && !this.canWelfare && !this.canAdmin) return;
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listUnusedLeaveClaims");
        fd.append("view", this.tab);
        const res = await execute(fd, authUrl);
        if (res.data.success) this.claims = res.data.data?.claims || [];
        else this.claims = [];
      } catch (_) {
        this.claims = [];
      } finally {
        this.loading = false;
      }
    },
    openAct(claim, action) {
      this.modal = { open: true, claim, action, remarks: "", saving: false };
    },
    async confirmAct() {
      if (!this.modal.claim) return;
      if (String(this.modal.remarks).trim().length < 3 && !String(this.modal.action).includes("approve")) {
        alert("Please enter at least 3 characters (reason / comments).");
        return;
      }
      this.modal.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "processUnusedLeaveClaim");
        fd.append("claimId", String(this.modal.claim.id));
        fd.append("action", this.modal.action);
        fd.append("remarks", String(this.modal.remarks).trim());
        const res = await execute(fd, authUrl);
        if (!res.data.success) {
          alert(res.data.message || "Failed");
          return;
        }
        alert(res.data.message || "Done");
        this.modal.open = false;
        await this.load();
      } catch (e) {
        alert(e?.response?.data?.message || e.message || "Error");
      } finally {
        this.modal.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.tab {
  border: 1px solid #cbd5e1;
  background: #fff;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: #334155;
}
.tab.on {
  background: #0c2d48;
  color: #fff;
  border-color: #0c2d48;
}
.card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
}
.card.warn {
  border-color: #fecdd3;
  background: #fff1f2;
  color: #9f1239;
}
.toolbar {
  margin-bottom: 10px;
}
.muted {
  color: #64748b;
}
.small {
  font-size: 12px;
}
.table-wrap {
  overflow: auto;
  max-height: 560px;
}
.tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.tbl th,
.tbl td {
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 6px;
  vertical-align: top;
}
.link {
  background: none;
  border: none;
  color: #1d4ed8;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
}
.link.bad {
  color: #b91c1c;
}
.btn {
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.ghost {
  background: #fff;
  border-color: #cbd5e1;
  color: #334155;
}
.primary {
  background: linear-gradient(180deg, #1e4976, #0c2d48);
  color: #fff;
}
.modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}
.modal-card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  width: 92%;
  max-width: 420px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}
.lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin: 10px 0 4px;
}
.inp {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 8px 10px;
}
.ta {
  resize: vertical;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}
</style>
